import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import {
  Alert,
  Avatar,
  Box,
  Card,
  Chip,
  InputAdornment,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import LockIcon from '@mui/icons-material/Lock'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver'
import API from '../../../services/API'
import { TenantSettingsContext } from '../../../App'
import EmptyState from '../../../components/EmptyState/EmptyState'
import { SPEAK_UP_STATUSES, statusColor } from './speakUpConstants'

const PRESETS = [
  {
    key: 'open',
    label: 'Open',
    caption: 'Awaiting action',
    statuses: ['New', 'Under Review'],
    tone: 'warning',
  },
  {
    key: 'new',
    label: 'New',
    caption: 'Not yet reviewed',
    statuses: ['New'],
    tone: 'info',
  },
  {
    key: 'review',
    label: 'Under Review',
    caption: 'Being looked into',
    statuses: ['Under Review'],
    tone: 'warning',
  },
  {
    key: 'closed',
    label: 'Closed',
    caption: 'Outcome recorded',
    statuses: ['Closed'],
    tone: 'success',
  },
  {
    key: 'all',
    label: 'All Concerns',
    caption: 'Everything on record',
    statuses: [],
    tone: 'primary',
  },
]

const countFor = (summary, preset) =>
  !summary
    ? null
    : preset.statuses.length
      ? preset.statuses.reduce((sum, s) => sum + (summary[s] ?? 0), 0)
      : summary.total

const PresetTile = ({ preset, count, active, onClick }) => (
  <Card
    variant="outlined"
    onClick={onClick}
    sx={{
      p: 1.5,
      borderRadius: 2.5,
      cursor: 'pointer',
      minWidth: 0,
      borderColor: active ? 'primary.main' : 'divider',
      borderWidth: active ? 2 : 1,
      bgcolor: active ? 'action.hover' : 'background.paper',
      transition: 'all 0.15s ease',
      '&:hover': { borderColor: 'primary.light' },
    }}
  >
    <Typography
      component="div"
      variant="caption"
      noWrap
      sx={{
        color: 'text.secondary',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        fontSize: '0.66rem',
      }}
    >
      {preset.label}
    </Typography>
    <Stack direction="row" spacing={0.75} alignItems="center">
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: `${preset.tone}.main`,
        }}
      />
      <Typography sx={{ fontWeight: 800, fontSize: '1.1rem' }} noWrap>
        {count ?? '—'}
      </Typography>
    </Stack>
    <Typography component="div" variant="caption" color="text.secondary" noWrap>
      {preset.caption}
    </Typography>
  </Card>
)

export const ReporterCell = ({ concern, dense }) =>
  concern.anonymous ? (
    <Stack direction="row" alignItems="center" gap={0.75}>
      <LockIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
      <Typography variant={dense ? 'body2' : 'body1'} color="text.secondary">
        Anonymous
      </Typography>
    </Stack>
  ) : (
    <Stack direction="row" alignItems="center" gap={0.75}>
      <Avatar
        src={concern.reporter?.photograph}
        sx={{ width: 22, height: 22 }}
      />
      <Typography variant={dense ? 'body2' : 'body1'}>
        {concern.reporter
          ? `${concern.reporter.firstName} ${concern.reporter.lastName}`
          : 'Named reporter'}
      </Typography>
    </Stack>
  )

// Governance-side register of Freedom to Speak Up concerns.
const SpeakUpConcerns = () => {
  const navigate = useNavigate()
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig ?? {}
  const [loading, setLoading] = useState(true)
  const [concerns, setConcerns] = useState([])
  const [summary, setSummary] = useState(null)
  const [activePreset, setActivePreset] = useState('open')
  const [search, setSearch] = useState('')

  const preset = PRESETS.find((p) => p.key === activePreset) ?? PRESETS[0]

  useEffect(() => {
    setLoading(true)
    API.get('/speak-up-concerns', {
      params: { statuses: preset.statuses.join(',') },
    })
      .then((res) => {
        if (res.status === 200) {
          setConcerns(res.data.concerns ?? [])
          setSummary(res.data.summary ?? null)
        }
      })
      .finally(() => setLoading(false))
  }, [activePreset])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return concerns
    return concerns.filter((c) =>
      [c.referenceCode, c.category, c.description, c.whereHappened]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    )
  }, [concerns, search])

  const disabled = orgSettings?.speakUpSettings?.enabled === false

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Stack direction="row" gap={1}>
        <RecordVoiceOverIcon color="primary" sx={{mt: 0.8}}/>
        <Stack>
          <Typography variant="title2">Speak Up Concerns</Typography>
          <Typography variant="caption" color="text.secondary">
            Concerns raised by staff through Freedom to Speak Up. Anonymous
            concerns carry no record of who raised them.
          </Typography>
        </Stack>
      </Stack>

      {disabled && (
        <Alert severity="warning">
          Freedom to Speak Up is currently switched off for your organisation,
          so staff cannot raise new concerns. Existing concerns are still shown
          here. Re-enable it under Configuration → Governance → Speak Up.
        </Alert>
      )}

      <Box
        display="grid"
        gap={1.5}
        sx={{
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(5, 1fr)',
          },
        }}
      >
        {PRESETS.map((p) => (
          <PresetTile
            key={p.key}
            preset={p}
            count={countFor(summary, p)}
            active={activePreset === p.key}
            onClick={() => setActivePreset(p.key)}
          />
        ))}
      </Box>

      <TextField
        size="small"
        placeholder="Search by reference, category or wording"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ maxWidth: 420 }}
      />

      {loading && <LinearProgress />}

      {!loading && filtered.length === 0 ? (
        <EmptyState
          title="No concerns here"
          description={
            search
              ? 'Nothing matches your search.'
              : 'Nothing has been raised under this filter. Staff can raise a concern from the Freedom to Speak Up page or the mobile app.'
          }
        />
      ) : (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell>Reference</TableCell>
                <TableCell>Raised</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Concern</TableCell>
                <TableCell>Raised by</TableCell>
                <TableCell>Assigned</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((c) => (
                <TableRow
                  key={c._id}
                  hover
                  onClick={() => navigate(`/speak-up-concerns/${c._id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {c.referenceCode}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {moment(c.createdAt).format('DD MMM YYYY')}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {c.category}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <Typography variant="body2" noWrap title={c.description}>
                      {c.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <ReporterCell concern={c} dense />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {c.assignedTo?.userAccount
                      ? `${c.assignedTo.userAccount.firstName} ${c.assignedTo.userAccount.lastName}`
                      : '—'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={c.status}
                      color={statusColor(c.status)}
                      variant={c.status === 'Closed' ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
      <Typography variant="caption" color="text.secondary">
        Statuses: {SPEAK_UP_STATUSES.join(' → ')}. Closing a concern records the
        outcome against it.
      </Typography>
    </Box>
  )
}

export default SpeakUpConcerns
